// src/features/appointments/AppointmentsPage.tsx
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Calendar, Search, AlertCircle, XCircle, Eye } from 'lucide-react';
import { appointmentService } from '../../api/services';
import { AppointmentStatus } from '../../api/types';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const STATUS_LABELS: Record<AppointmentStatus, string> = {
  PENDING: 'Pending Review',
  CONFIRMED: 'Confirmed',
  CHECKED_IN: 'Checked In',
  IN_CONSULTATION: 'In Consultation',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  REJECTED: 'Rejected',
};

export function AppointmentsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [cancelId, setCancelId] = useState<number | null>(null);

  const { data: appointments = [], isLoading } = useQuery({
    queryKey: ['my-appointments'],
    queryFn: appointmentService.getMyAppointments,
  });

  const { mutate: cancelAppt, isPending: cancelling } = useMutation({
    mutationFn: (id: number) => appointmentService.cancel(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-appointments'] });
      setCancelId(null);
      toast.success('Appointment cancelled');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const filtered = appointments.filter((a) => {
    const matchSearch =
      !search ||
      a.appointmentNumber.includes(search) ||
      a.doctorName.toLowerCase().includes(search.toLowerCase()) ||
      a.departmentName.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !statusFilter || a.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const canCancel = (status: string) =>
    ['PENDING', 'CONFIRMED'].includes(status);

  return (
    <div className="max-w-5xl mx-auto px-6 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-brand-600" />
            My Appointments
          </h1>
          <p className="text-sm text-slate-500 mt-1">{appointments.length} total appointments</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-5">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              className="input pl-9"
              placeholder="Search by number or doctor…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="input min-w-[160px]"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Appointment list */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card p-5 space-y-3">
              <div className="skeleton h-4 w-1/4" />
              <div className="skeleton h-3 w-3/4" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">
            {appointments.length === 0
              ? 'No appointments yet. Book your first one!'
              : 'No appointments match your filters'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((appt) => (
            <div
              key={appt.id}
              className="card p-5 hover:shadow-card-hover transition-all duration-150 animate-fade-up"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-xs font-semibold text-brand-700 bg-brand-50 px-2 py-0.5 rounded">
                      {appt.appointmentNumber}
                    </span>
                    <span className={`status-${appt.status}`}>
                      {STATUS_LABELS[appt.status]}
                    </span>
                    {appt.tokenNumber && (
                      <span className="badge bg-slate-100 text-slate-700">
                        Token #{appt.tokenNumber}
                      </span>
                    )}
                  </div>

                  <div className="mt-2">
                    <div className="font-semibold text-slate-900">{appt.doctorName}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {appt.departmentName}
                      {appt.sessionType ? ` · ${appt.sessionType} session` : ''}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {appt.isOnCallBooking && !appt.appointmentDate
                        ? <span className="text-amber-600 font-medium">Date to be confirmed</span>
                        : appt.appointmentDate
                          ? format(parseISO(appt.appointmentDate), 'EEE, MMM d yyyy')
                          : '–'}
                    </span>
                    <span className="text-slate-300">·</span>
                    <span>Booked {format(parseISO(appt.createdAt), 'MMM d')}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {canCancel(appt.status) && (
                    <button
                      onClick={() => setCancelId(appt.id)}
                      className="btn btn-ghost btn-sm text-red-600 hover:bg-red-50"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cancel confirm dialog */}
      {cancelId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm">
          <div className="card w-full max-w-sm mx-4 p-6 shadow-floating animate-slide-in">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Cancel Appointment?</h3>
                <p className="text-xs text-slate-500 mt-0.5">This action cannot be undone</p>
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={() => setCancelId(null)} className="btn btn-secondary flex-1 justify-center">
                Keep it
              </button>
              <button
                onClick={() => cancelAppt(cancelId)}
                disabled={cancelling}
                className="btn btn-danger flex-1 justify-center"
              >
                {cancelling ? 'Cancelling…' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
